import type { Scenario } from '@/features/playbook/types';

export const scenarios: Scenario[] = [
  {
    slug: 'wounded-traveller',
    title: 'The Wounded Traveller on the Road',
    setup: "A merchant's man has collapsed near the covenant boundary, blood seeping through his tunic. You're the closest with medical knowledge. His companion is panicked, asking if he'll live. You have perhaps an hour before infection sets in, and no certainty the covenant will support the cost of care.",
    rolls: [
      { ability: 'medicine', note: 'assess the wound—is it cleanly made or torn?' },
      { ability: 'chirurgy', note: 'bind it well to slow the bleeding' },
      { ability: 'survival', note: 'gather supplies if no proper kit is near' },
    ],
    tip: 'Mikaela can treat carefully without claiming to cure—humility suits her here.',
  },
  {
    slug: 'audience-comtesse',
    title: 'Audience with the Comtesse',
    setup: "A local noblewoman has sent word she wishes to meet the covenant's scribe about a disputed marriage contract. The meeting is formal, the room full of courtiers, and you sense she is testing your patron through you. She speaks quickly, expects quick answers, and shows mild disdain for anyone who stumbles.",
    rolls: [
      { ability: 'etiquette', note: 'avoid offending her pride—curtsy, call her rightly' },
      { ability: 'bargain', note: 'negotiate what records the covenant will share' },
      { ability: 'concentration', note: 'remember the details of the contract law you studied' },
    ],
    tip: "Mikaela's caution makes her a safer choice than a flashier mage—play that angle.",
  },
  {
    slug: 'library-crisis',
    title: 'The Library Crisis',
    setup: "A messenger arrives: the covenant's main archive shows water damage overnight. You arrive to find the oldest books—ledgers, histories, grimoire annotations—slowly soaking. A junior sodalis is panicking. You have an hour before the real damage becomes irreversible, and the soaked wood door to the vault is swollen shut.",
    rolls: [
      { ability: 'profession-scribe', note: 'which books matter most—save the rarest first' },
      { ability: 'concentration', note: 'keep focus while helping calm the panicking youth' },
      { ability: 'survival', note: 'improvise drying, ventilation, or a way to open the door' },
    ],
    tip: "Her pride in the covenant's records makes this personal—Perfectionist trait shines here.",
  },
  {
    slug: 'shady-bookstall',
    title: 'A Book in a Shady Stall',
    setup: "In a Paris market, you spot a worn volume in a peddler's stall—the spine bears a sigil you recognize as belonging to a dead magus. The peddler claims ignorance of its source. The book smells of salt and sea; it may have survived a shipwreck or a theft. Buying it could anger a rival covenant, but leaving it in ignorant hands feels wrong.",
    rolls: [
      { ability: 'bargain', note: 'negotiate price without revealing how much you know' },
      { ability: 'magic-lore', note: 'recall which covenant the sigil belongs to' },
      { ability: 'awareness', note: 'is anyone watching—a rival buyer, perhaps?' },
    ],
    tip: "Curiosity and caution war in her; she'll likely buy cautiously and report back.",
  },
  {
    slug: 'lost-in-paris',
    title: 'Lost in Paris Alleys',
    setup: "An errand took you off the main roads near the Seine. Now dusk is falling, the alleys twist unfamiliar, and you've taken a wrong turn twice. Your knowledge of Paris is shallow. A drunk beggar laughs at your confusion. Ahead, voices—a gang of young men blocking the way, not obviously hostile but unpredictable.",
    rolls: [
      { ability: 'lore-paris', note: 'does this alley name match anything you know?' },
      { ability: 'folk-ken', note: 'read the gang—are they locals, thieves, or just rough lads?' },
      { ability: 'awareness', note: 'spot landmarks, a church spire, anything to reorient' },
    ],
    tip: 'Her caution and low Presence mean avoidance or politeness work better than bluffing.',
  },
  {
    slug: 'hedge-magician-tavern',
    title: 'The Hedge Magician in the Tavern',
    setup: "A woman sits alone at a corner table, fingers dancing through odd gestures, humming softly. The tavern keeper whispers she's been here three nights, and no one knows her name. When she notices you watching, she smiles and signals you over. Something in her manner—fluid, strange—marks her as touched by the Art. She wants to talk to you, not anyone else.",
    rolls: [
      { ability: 'magic-lore', note: 'what tradition does her gesture-work suggest?' },
      { ability: 'folk-ken', note: "gauge if she's dangerous or simply lonely" },
      { ability: 'concentration', note: 'listen carefully, note details for your superiors' },
    ],
    tip: 'Her Magical Fascination is a flaw here—she may trust too easily; use Common Sense to check.',
  },
  {
    slug: 'covenant-quarrel-scribe',
    title: 'Scribe for a Covenant Quarrel',
    setup: "Two senior mages are in sharp dispute over resource allocation, and the Covenant Master has asked you to draft a fair summary of each side's claim for the Record. One magus is intimidating, the other is your patron's friend. Both press you to slant the language their way. You must write truth without angering either—and the Record is binding custom.",
    rolls: [
      { ability: 'artes-liberales', note: 'frame both sides fairly—grammar and logic matter' },
      { ability: 'concentration', note: 'keep your nerve under pressure from both parties' },
      { ability: 'bargain', note: "agree to small phrasing compromises that don't betray truth" },
    ],
    tip: 'Perfectionist and Humble traits make her ideal, but the strain will exhaust her mentally.',
  },
  {
    slug: 'dinner-heresy',
    title: 'Dangerous Philosophy at Dinner',
    setup: 'A covenant dinner with visiting scholars includes a spirited debate on the nature of the soul and whether demons truly possess free will. One guest—a sharp-tongued academic—pushes toward ideas the Church would call heresy. The table quiets, watching to see who speaks. The Covenant Master is silent. All eyes drift to you, the scribe—expected to record, expected to know such subtleties.',
    rolls: [
      { ability: 'philosophiae', note: 'understand the theological stakes well enough to say something wise' },
      { ability: 'latin', note: 'cite an authority correctly if you choose to speak in that tongue' },
      { ability: 'etiquette', note: 'if you must intervene, do so without seeming to lecture' },
    ],
    tip: 'She can stay silent—humility and caution make that a valid choice too.',
  },
  {
    slug: 'forest-road-twilight',
    title: 'Forest Road at Twilight',
    setup: "You're traveling alone between covenants, a packet of letters in your satchel. The forest road grows dim as the sun drops. Your breath comes harder than it should—always does on long roads. Ahead, a figure sits beneath a tree, very still. Not moving. Not breathing, perhaps. The figure wears a cloak you don't recognize, and the shadows around it seem too deep.",
    rolls: [
      { ability: 'awareness', note: 'study the figure—human, animal, or something else?' },
      { ability: 'survival', note: 'is there an alternate route through the trees?' },
      { ability: 'magic-lore', note: 'does the wrongness of the shadows suggest the Art?' },
    ],
    tip: 'Short of Breath makes flight difficult; caution and observation are her strengths.',
  },
  {
    slug: 'midnight-translation',
    title: 'The Midnight Translation',
    setup: "A visitor magus arrives at midnight with an old document in a language fragment—part Greek, part Latin, part something older. She needs a translation by dawn for a working she's prepared. The document is fragile, the light is poor, and your eyes are already tired. She offers good silver. She also watches your every word, skeptical of your knowledge, ready to correct you.",
    rolls: [
      { ability: 'latin', note: 'parse the Latin sections—are they classical or corrupted?' },
      { ability: 'artes-liberales', note: "recognize the older tongue's roots if it touches grammar lore" },
      { ability: 'concentration', note: 'keep precision through fatigue and pressure' },
    ],
    tip: "Perfectionist means she'll stay until it's right; Common Sense means she'll ask for help.",
  },
];
